import React, { PureComponent, useState, useRef, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { withResizeDetector } from 'react-resize-detector';

import { makeGetTask, makeIsDependant, makeGetFilteredTask } from '../selectors/tasks';
import { makeGetMilestone } from '../selectors/milestones';
import { makeGetTaskNotifications } from '../selectors/notifications';
import { makeGetProject } from '../selectors/projects';

import { fetchTask, updateTask, assignTask, unassignTask } from '../actions/tasks';

import { userRoles } from '../constants';
import { getUserFriendlyRoles, isTempId } from '../utils/helpers';
import { canUser } from '../services/permissions-service';
import { trackEvent } from '../services/events-tracker-service';

import TaskCompleteToggle from 'components/TaskCompleteToggle';
import MultiLineInput from 'components/MultiLineInput';
import TaskNiceId from 'components/TaskNiceId';
import CanUser from './can-user';
import TaskActions from './task-actions';
import MilestoneName from './milestone-name';
import ProjectName from './project-name';
import UsersList from './users-list';
import LabelsList from './labels-list';
import Tooltip from './tooltip';
import NiftyMarkdown from './nifty-markdown';
import DateTime from 'components/DateTime';

import IconRoadmap from '../icons/icon-roadmap';
import IconSubtask from '../icons/icon-subtask';
import IconFile from '../icons/icon-file';
import IconDiscussionAlt from '../icons/icon-discussion-alt';
import IconEye from '../icons/icon-eye';
import IconCalendarSimple from '../icons/icon-calendar-simple';
import IconRepeat from '../icons/icon-repeat';
import IconProject from '../icons/icon-project';

const TaskInner = ({ id, task, isTemp, showActions, notificationsCount, projectDisabledModules, milestone, dependantOn, labelsFetched, simpleTask, publicPage, showProject, height, onClick, onCompleteToggle, onAssigneeRemove, onAssigneeAdd, onHeightChange }) => {
    projectDisabledModules = (!projectDisabledModules ? [] : projectDisabledModules);
    const [editing, setEditing] = useState(false);
    const nameInputCtrl = useRef();

    useEffect(() => {
        if (height) {
            onHeightChange(height + 30 + 8);
        }
    }, [height, onHeightChange]);

    const handleEditNameClick = useCallback(() => {
        setEditing(true);

        requestAnimationFrame(() => {
            nameInputCtrl.current && nameInputCtrl.current.focus(true);
        })
    }, [nameInputCtrl, setEditing]);

    const handleNameBlur = useCallback(nameValue => {
        setEditing(false);

        if (!nameValue) {
            nameInputCtrl.current && nameInputCtrl.current.reset(true);
            return;
        }

        updateTask(id, { name: nameValue });
    }, [id, nameInputCtrl, setEditing]);

    return (
        <div className="task-inner" data-notification={notificationsCount}>
            <div className="task-overlay" onClick={onClick} />

            {
                showActions ? (
                    <CanUser type="editTask">
                        <TaskActions
                            id={id}
                            milestone={milestone}
                            projectId={task.project}
                            onEditNameClick={handleEditNameClick}
                        />
                    </CanUser>
                ) : null
            }

            <div className="task-top">
                {
                    task.nice_id ? (
                        <span className="task-id-holder">
                            <TaskNiceId niceId={task.nice_id} />
                        </span>
                    ) : null
                }

                {
                    labelsFetched && task.labels.length ? (
                        <div className="task-labels">
                            <LabelsList labels={task.labels} inverted={true} />
                        </div>
                    ) : null
                }
            </div>

            <div className="task-name">
                <CanUser type="editTask" customList={[task.created_by]}>
                    <div className="task-complete-toggle-holder">
                        <TaskCompleteToggle
                            dependantOn={dependantOn}
                            completed={task.completed}
                            disabled={isTemp}
                            projectId={task.project}
                            onChange={onCompleteToggle}
                        />
                    </div>
                </CanUser>

                <MultiLineInput
                    className="task-name-text"
                    placeholder="Add task name…"
                    canEdit={editing}
                    value={task.name}
                    provideController={nameInputCtrl}
                    onBlur={handleNameBlur}
                />
            </div>

            {
                !simpleTask && task.banner ? (
                    <div className="task-banner">
                        <img src={task.banner.replace('w-360,h-360', 'w-500,h-360')} alt="Task banner" />
                    </div>
                ) : null
            }

            {
                task.description ? (
                    <div className="task-description">
                        <NiftyMarkdown unstyled={true}>{task.description}</NiftyMarkdown>
                    </div>
                ) : null
            }

            <div className="task-foot">
                <div className="meta">
                    {
                        !publicPage && showProject ? (
                            <div className="meta-item">
                                <span className="meta-icon">
                                    <IconProject />
                                </span>

                                <span className="meta-text">
                                    <ProjectName id={task.project} />
                                </span>
                            </div>
                        ) : !publicPage && task.milestone && !projectDisabledModules.includes('milestones') ? (
                            <div className="meta-item">
                                <span className="meta-icon">
                                    <IconRoadmap />
                                </span>

                                <span className="meta-text">
                                    <MilestoneName id={task.milestone} />
                                </span>
                            </div>
                        ) : null
                    }

                    {
                        task.due_date ?
                            <div className={'meta-item' + (moment().diff(task.due_date) > 0 && !task.completed ? ' text-warning' : '')}>
                                <span className="meta-icon">
                                    <IconCalendarSimple />
                                </span>

                                <span className="meta-text">
                                    <DateTime format="date">{task.due_date}</DateTime>
                                </span>
                            </div>
                            : null
                    }

                    {
                        task.total_subtasks ? (
                            <div className={'meta-item' + (task.total_subtasks === task.completed_subtasks ? ' active' : '')}>
                                <span className="meta-icon">
                                    <IconSubtask />
                                </span>

                                <span className="meta-text">
                                    {task.completed_subtasks}/{task.total_subtasks}
                                </span>
                            </div>
                        ) : null
                    }

                    {
                        task.comments ?
                            <div className="meta-item">
                                <span className="meta-icon" style={{marginRight: '2px'}}>
                                    <IconDiscussionAlt />
                                </span>

                                <span className="meta-text">
                                    { task.comments }
                                </span>
                            </div>
                            : null
                    }

                    {
                        task.attachments && task.attachments.length ?
                            <div className="meta-item">
                                <span className="meta-icon">
                                    <IconFile />
                                </span>

                                <span className="meta-text">
                                    { task.attachments.length }
                                </span>
                            </div>
                            : null
                    }

                    {
                        task.hidden?.length ?
                            <Tooltip tooltip={`This task is hidden from ${getUserFriendlyRoles(task.hidden, userRoles())}`}>
                                <div className="meta-item with-tooltip">
                                    <span className="meta-icon meta-icon-single">
                                        <IconEye />
                                    </span>
                                </div>
                            </Tooltip>
                            : null
                    }

                    {
                        !task.archived && task.rule ?
                            <Tooltip tooltip="This task is recurring">
                                <div className="meta-item with-tooltip">
                                    <span className="meta-icon meta-icon-single">
                                        <IconRepeat size="12px" />
                                    </span>
                                </div>
                            </Tooltip>
                            : null
                    }
                </div>

                {
                    !isTemp && ((task.assignees && task.assignees.length) || canUser('assignToTask', [task.created_by])) ? (
                        <div className="meta-item">
                            <UsersList
                                popoutTitle="ASSIGN TASK TO:"
                                projectId={task.project}
                                editable={!task.completed && !task.archived && canUser('assignToTask', [task.created_by])}
                                size="xsmall"
                                border={true}
                                users={task.assignees}
                                roles={userRoles()}
                                maxUsersCount={2}
                                popoutSize="xxsmall"
                                subtitles={canUser('unassignFromTask', [task.created_by]) ? 'Un-assign user' : null}
                                emailMatchText="Select user role above and click “Invite” below and we’ll shoot this person an email notifying them that they’ve been added to this task."
                                noMatchText="Looks like that person isn’t a member yet. Enter their email address to add them to the task and project."
                                closeOnEveryClick={true}
                                onSubtitleClick={onAssigneeRemove}
                                excludedUsers={task.assignees}
                                onUserSelect={onAssigneeAdd}
                                addUserTooltip="Add assignee"
                            />
                        </div>
                    ) : null
                }
            </div>
        </div>
    )
};

const TaskInnerWithResize = withResizeDetector(TaskInner);

class Task extends PureComponent {
    static defaultProps = {
        showActions: true
    };

    handleTaskClick = () => {
        if (!this.props.id || this.props.isTemp) {
            return;
        }

        this.props.onClick && this.props.onClick(this.props.task.nice_id || this.props.id);
    }

    handleAssigneeRemove = userId => {
        unassignTask(this.props.id, [userId]);
    }

    handleAssigneeAdd = user => {
        assignTask(this.props.id, [user.id || user]);
    }

    handleTaskCompleteToggle = async (completed, taskGroupId) => {
        if (this.props.isTemp) {
            return;
        }

        const nextProps = {
            completed,
            task_group: taskGroupId
        }

        if (completed) {
            nextProps.completed_on = new Date();
        }

        try {
            this.updateTask(nextProps);

            if (completed) {
                trackEvent('TASK.COMPLETE');
            }
        } catch (e) {
            // es lint
        }
    }

    handleHeightChange = height => {
        this.props.onHeightChange && this.props.onHeightChange(this.props.index, height);
    }

    updateTask(taskProps, preventSave = false) {
        return updateTask(this.props.id, taskProps, preventSave);
    }

    fetchTask() {
        fetchTask(this.props.id);
    }

    attemptFetch() {
        if (this.props.id && !this.props.task && !this.props.filtered) {
            this.fetchTask();
        }
    }

    get className() {
        const className = ['task'];
        if (this.props.isDragging) className.push('is-dragging');
        if (this.props.current) className.push('active');
        if (this.props.isOver) className.push('is-over');
        if (this.props.task.archived) className.push('archived');
        if (this.props.task.completed) className.push('completed');
        if (this.props.draggedTask === this.props.id) className.push('dragged-task');
        if (this.props.simpleTask) className.push('simple-task');
        if (this.props.dependantOn) className.push('dependant');

        return className.join(' ');
    }

    componentDidMount() {
        this.attemptFetch();
    }

    componentDidUpdate(prevProps) {
        this.attemptFetch();
    }

    render() {
        const { task, isTemp, provided, style, showActions, notificationsCount, projectDisabledModules, milestone, dependantOn, simpleTask, labelsFetched, publicPage, showProject } = this.props;

        if (!task) {
            return null;
        }

        let props = {
            className: this.className,
            style
        };

        if (provided) {
            props = Object.assign(props, {
                ref: provided.innerRef,
                ...provided.draggableProps,
                ...provided.dragHandleProps,
                style: style ? { ...provided.draggableProps.style, ...style } : provided.draggableProps.style
            });
        }

        const taskProps = {
            id: task.id,
            task: task,
            isTemp: isTemp,
            showActions: showActions,
            notificationsCount: notificationsCount,
            projectDisabledModules: projectDisabledModules,
            milestone: milestone,
            dependantOn: dependantOn,
            simpleTask: simpleTask,
            labelsFetched: labelsFetched,
            publicPage: publicPage,
            showProject: showProject,
            onClick: this.handleTaskClick,
            onCompleteToggle: this.handleTaskCompleteToggle,
            onAssigneeRemove: this.handleAssigneeRemove,
            onAssigneeAdd: this.handleAssigneeAdd,
            onHeightChange: this.handleHeightChange
        }

        if (provided) {
            return (
                <div {...props}>
                    <TaskInnerWithResize {...taskProps} />
                </div>
            );
        }

        return (
            <div {...props}>
                <TaskInner {...taskProps} />
            </div>
        );
    };
}

const makeMapStateToProps = () => {
    const getTask = makeGetTask();
    const getMilestone = makeGetMilestone();
    const isDependant = makeIsDependant();
    const getTaskNotifications = makeGetTaskNotifications();
    const getFilteredTask = makeGetFilteredTask();
    const getProject = makeGetProject();

    return ({ projects, tasks, notifications, milestones }, ownProps) => {
        const task = ownProps.id && getTask(tasks.items, ownProps.id);
        const filteredTask = task && getFilteredTask(task, tasks.filter);
        const project = task && getProject(projects.items, task.project);

        return {
            task: filteredTask,
            milestone: project && !(project.disabled_modules || []).includes('milestones') && task.milestone && getMilestone(milestones.items, task.milestone),
            isTemp: isTempId(ownProps.id),
            projectDisabledModules: project?.disabled_modules,
            dependantOn: isDependant(ownProps.id, true, tasks.items, milestones.items),
            filtered: !filteredTask && task,
            notificationsCount: (ownProps.id ? getTaskNotifications(notifications.items, ownProps.id) : []).length
        }
    }
}

export default connect(makeMapStateToProps)(Task);
